import { useMemo } from 'react';
import { ListOrderIdMap } from '../../types';
import { useDeepMemoize } from './use-deep-memoize';

type ListItem<T, K extends keyof T> = { [key in K]: T[K] };

type UseReOrderedListProps<T extends ListItem<T, K>, K extends keyof T> = {
  list: T[];
  listOrderIdMap: ListOrderIdMap;
  idKey: K;
};

export const useReorderedList = <T extends ListItem<T, K>, K extends keyof T>({
  list: originalList,
  listOrderIdMap,
  idKey,
}: UseReOrderedListProps<T, K>) => {
  const memoizedList = useDeepMemoize(originalList);
  const reorderedList = useMemo(
    () => reorderColumnsUsingMap(memoizedList, listOrderIdMap, idKey),
    [memoizedList, listOrderIdMap, idKey]
  );

  return {
    reorderedList,
  };
};

/**
 *
 * @param list a lsit of items with id property
 * @param orderMap order map defining the order of items with id as key and index as value
 * @returns the reordered list based on the order map
 */
export const reorderColumnsUsingMap = <T extends ListItem<T, K>, K extends keyof T>(
  list: T[],
  orderMap: ListOrderIdMap,
  idKey: K
): T[] => {
  return [...list].sort((a, b) => {
    const aId = a[idKey] || undefined;
    const bId = b[idKey] || undefined;
    const orderA = (aId && orderMap[aId]) ?? Number.MAX_SAFE_INTEGER;
    const orderB = (bId && orderMap[bId]) ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });
};
